import {useEffect,useMemo,useState,type FormEvent} from 'react';
import {CatalogStatusSwitch} from './CatalogStatusSwitch';
type Product={id:string;category:string;title:string;sku:string;
  orderingUrl:string;details:string;receiptMessage:string;trackInventory:boolean;
  usedInShop:boolean;forSale:boolean;priceCents:number;daysBetweenCounts:number|null;
  warnAtQuantity:number|null;enabled:boolean};
type Draft=Omit<Product,'daysBetweenCounts'|'warnAtQuantity'>;
const price=(cents:number)=>new Intl.NumberFormat('en-US',{
  style:'currency',currency:'USD'}).format(cents/100);
const csvValue=(s:string)=>/[",\r\n]/.test(s)?'"'+s.replace(/"/g,'""')+'"':s;
export function productCsv(products:Product[]){
  const headers=['id','Category','Title','SKU','Used in Shop','For Sale',
    'Price','Days Between Counts','Warn at Quantity'];
  const values=products.map(p=>[
    p.id,p.category,p.title,p.sku,p.usedInShop?'yes':'',
    p.forSale?'yes':'no',price(p.priceCents),
    p.daysBetweenCounts==null?'':String(p.daysBetweenCounts),
    p.warnAtQuantity==null?'':p.warnAtQuantity+' unit',
  ]);
  return [headers,...values].map(cols=>cols.map(csvValue).join(',')).join('\r\n')+'\r\n';
}
const blank=(category:string):Draft=>({
  id:'',category,title:'',sku:'',orderingUrl:'',details:'',receiptMessage:'',
  trackInventory:false,usedInShop:false,forSale:true,priceCents:0,enabled:true,
});
export function ProductsWorkspace({locationCode}:{locationCode:string}){
  const [items,setItems]=useState<Product[]>([]);
  const [categories,setCategories]=useState<string[]>([]);
  const [canEdit,setCanEdit]=useState(false);
  const [status,setStatus]=useState<'loading'|'ready'|'error'>('loading');
  const [query,setQuery]=useState('');
  const [state,setState]=useState('Enabled');
  const [kind,setKind]=useState('All');
  const [category,setCategory]=useState('');
  const [order,setOrder]=useState('Name A-Z');
  const [view,setView]=useState<'products'|'categories'>('products');
  const [draft,setDraft]=useState<Draft|null>(null);
  const [amount,setAmount]=useState('');
  const [saving,setSaving]=useState(false);
  const [statusBusy,setStatusBusy]=useState<string|null>(null);
  const [notice,setNotice]=useState('');
  const [categoryDraft,setCategoryDraft]=useState<{original:string;value:string}|null>(null);
  const load=async(signal?:AbortSignal)=>{
    const q=new URLSearchParams({location:locationCode});
    const [products,cats]=await Promise.all([
      fetch('/api/v1/staff/products?'+q,{credentials:'same-origin',cache:'no-store',signal}),
      fetch('/api/v1/staff/product-categories?'+q,{credentials:'same-origin',cache:'no-store',signal}),
    ]);
    if(!products.ok)throw Error('Product catalog unavailable');
    const p=await products.json() as {ok:boolean;data?:{products:Product[]}};
    if(!p.ok||!p.data?.products)throw Error('Invalid product catalog');
    if(signal?.aborted)return;
    setItems(p.data.products);
    if(cats.ok){
      const c=await cats.json() as {ok:boolean;data?:{categories:string[];canEdit:boolean}};
      if(!signal?.aborted&&c.ok&&c.data){
        setCategories(c.data.categories);setCanEdit(c.data.canEdit);
      }
    }else{
      setCategories([...new Set(p.data.products.map(item=>item.category))].sort());
      setCanEdit(false);
    }
  };
  useEffect(()=>{
    const abort=new AbortController();setStatus('loading');setDraft(null);
    setView('products');setNotice('');setCanEdit(false);
    void load(abort.signal).then(()=>{if(!abort.signal.aborted)setStatus('ready');})
      .catch(()=>{if(!abort.signal.aborted)setStatus('error');});
    return ()=>abort.abort();
  },[locationCode]);
  const filtered=useMemo(()=>{
    const terms=query.trim().toLowerCase();
    return items.filter(p=>(state==='All'||(state==='Enabled'?p.enabled:!p.enabled))&&
      (kind==='All'||(kind==='Retail'?p.forSale:!p.forSale))&&
      (!category||p.category===category)&&
      (!terms||[p.id,p.category,p.title,p.sku].some(x=>x.toLowerCase().includes(terms))))
      .sort((a,b)=>order==='Name Z-A'?b.title.localeCompare(a.title):
        order==='Category'?a.category.localeCompare(b.category)||a.title.localeCompare(b.title):
        a.title.localeCompare(b.title));
  },[items,query,state,kind,category,order]);
  const exportCsv=()=>{
    const url=URL.createObjectURL(new Blob([productCsv(filtered)],
      {type:'text/csv;charset=utf-8'}));
    const link=document.createElement('a');link.href=url;
    link.download='products.csv';link.click();URL.revokeObjectURL(url);
  };
  const start=(product?:Product)=>{
    setDraft(product?{...product}:blank(categories[0]??''));
    setAmount(product?String(product.priceCents/100):'');
    setNotice('');window.scrollTo({top:0,behavior:'instant'});
  };
  const change=(patch:Partial<Draft>)=>setDraft(p=>p?{...p,...patch}:p);
  const save=async(event:FormEvent)=>{
    event.preventDefault();if(!draft||saving)return;
    const val=Number(amount);
    if(!Number.isFinite(val)||val<0||val>1000000||
      Math.round(val*100)!==val*100&&Math.abs(Math.round(val*100)-val*100)>0.00001){
      setNotice('Enter a valid price with no more than two decimal places.');return;
    }
    setSaving(true);setNotice('');
    try{
      const editing=!!draft.id;
      const resp=await fetch('/api/v1/staff/products',{
        method:editing?'PATCH':'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,...draft,
          ...(editing?{}:{id:undefined}),priceCents:Math.round(val*100)}),
      });
      if(!resp.ok)throw Error('Product could not be saved ('+resp.status+').');
      await load();setDraft(null);setNotice(editing?'Product saved.':'Product created.');
    }catch(e){setNotice(e instanceof Error?e.message:'Product could not be saved.');}
    finally{setSaving(false);}
  };
  const setProductEnabled=async(p:Product)=>{
    if(!canEdit||statusBusy||saving)return;
    setStatusBusy(p.id);setNotice('');
    try{
      const response=await fetch('/api/v1/staff/products/'+
        encodeURIComponent(p.id)+'/status',{
        method:'PATCH',credentials:'same-origin',cache:'no-store',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,enabled:!p.enabled}),
      });
      const body=await response.json() as {ok:boolean;error?:{code?:string}};
      if(!response.ok||!body.ok)throw Error(
        'Product status could not be saved ('+(body.error?.code??response.status)+').');
      await load();
      // Keep the changed item on screen, including when disabled.
      setState('All');setDraft(null);
      setNotice(p.title+(p.enabled?' disabled.':' enabled.'));
    }catch(error){
      setNotice(error instanceof Error?error.message:'Product status could not be saved.');
    }finally{setStatusBusy(null);}
  };
  const saveCategory=async(event:FormEvent)=>{
    event.preventDefault();if(!categoryDraft||saving)return;
    setSaving(true);setNotice('');
    const editing=!!categoryDraft.original;
    try{
      const resp=await fetch('/api/v1/staff/product-categories',{
        method:editing?'PATCH':'POST',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,
          name:editing?categoryDraft.original:categoryDraft.value,
          ...(editing?{newName:categoryDraft.value}:{})}),
      });
      if(!resp.ok)throw Error('Product category could not be saved ('+resp.status+').');
      await load();setCategoryDraft(null);setNotice('Product category saved.');
    }catch(e){setNotice(e instanceof Error?e.message:'Category could not be saved.');}
    finally{setSaving(false);}
  };
  const deleteCategory=async(name:string)=>{
    if(saving)return;
    setSaving(true);setNotice('');
    try{
      const resp=await fetch('/api/v1/staff/product-categories',{
        method:'DELETE',credentials:'same-origin',
        headers:{'Content-Type':'application/json'},
        body:JSON.stringify({location:locationCode,name}),
      });
      if(resp.status===409)throw Error('Category is in use by products.');
      if(!resp.ok)throw Error('Category could not be deleted.');
      await load();setNotice('Product category deleted.');
    }catch(e){setNotice(e instanceof Error?e.message:'Category could not be deleted.');}
    finally{setSaving(false);}
  };
  return <section className="ecfs-products" aria-label="Products">
    <header className="ecfs-products-heading">
      <h1>{view==='products'?'Products':'Product Categories'}</h1>
      {view==='products'?<>
        <span>inventory</span><span>orders</span>
        <button type="button" className="ecfs-products-header-link"
          onClick={()=>{setView('categories');setDraft(null);setNotice('');}}>
          product categories</button><span>vendors</span>
      </>:<button type="button" className="ecfs-products-header-link"
        onClick={()=>{setView('products');setCategoryDraft(null);setNotice('');}}>
        products</button>}
    </header>
    {notice&&<p role="status" aria-live="polite">{notice}</p>}
    {status==='loading'&&<p role="status">Loading products…</p>}
    {status==='error'&&<p role="alert">Products could not be loaded.</p>}
    {status==='ready'&&view==='categories'&&<>
      <div className="ecfs-products-toolbar">
        {canEdit&&<button type="button" className="ecfs-products-add"
          onClick={()=>setCategoryDraft({original:'',value:''})}>
          Add A Product Category</button>}
      </div>
      {categoryDraft&&<form className="ecfs-products-editor" onSubmit={e=>void saveCategory(e)}
        aria-label="Product category editor">
        <input aria-label="Product Category Name" required maxLength={160}
          value={categoryDraft.value} onChange={e=>setCategoryDraft(v=>v?
            {...v,value:e.target.value}:v)}/>
        <button type="submit" disabled={saving}>Save Product Category</button>
        <button type="button" onClick={()=>setCategoryDraft(null)}>cancel</button>
      </form>}
      <table className="ecfs-products-table"><thead><tr><th>Product Category</th>
        {canEdit&&<th>Actions</th>}</tr></thead><tbody>
        {categories.filter(name=>name.toLowerCase().includes(query.toLowerCase()))
          .map(name=><tr key={name}><td>
            <button type="button" className="ecfs-products-header-link"
              disabled={!canEdit} onClick={()=>setCategoryDraft({original:name,value:name})}>
              {name}</button></td>{canEdit&&<td>
              <button type="button" disabled={saving}
                aria-label={'Delete category '+name}
                onClick={()=>void deleteCategory(name)}>Delete</button></td>}</tr>)}
      </tbody></table>
    </>}
    {status==='ready'&&view==='products'&&<>
      <div className="ecfs-products-toolbar">
        {canEdit&&<button type="button" className="ecfs-products-add"
          onClick={()=>start()}>Add A Product</button>}
      </div>
      {draft&&canEdit&&<form className="ecfs-products-editor"
        aria-label={draft.id?'Edit Product':'Add Product'}
        onSubmit={e=>void save(e)}>
        <h2>{draft.id?'Edit '+draft.title:'Add A Product'}</h2>
        <label>Product Category <select value={draft.category} required
          onChange={e=>change({category:e.target.value})}>
          <option value="">Select Category</option>
          {categories.map(c=><option key={c}>{c}</option>)}
        </select></label>
        <label>Product name <input required maxLength={320} value={draft.title}
          onChange={e=>change({title:e.target.value})}/></label>
        <label>SKU (optional) <input value={draft.sku} maxLength={200}
          onChange={e=>change({sku:e.target.value})}/></label>
        <label>Current Ordering URL <input value={draft.orderingUrl}
          maxLength={2048} onChange={e=>change({orderingUrl:e.target.value})}/></label>
        <label>Product Details (optional) <textarea value={draft.details}
          onChange={e=>change({details:e.target.value})}/></label>
        <label>Sales Receipt Message for Customers (optional)
          <textarea value={draft.receiptMessage}
            onChange={e=>change({receiptMessage:e.target.value})}/>
        </label>
        <label><input type="checkbox" checked={draft.forSale}
          onChange={e=>change({forSale:e.target.checked})}/>For Sale in Shop</label>
        {draft.forSale&&<label>Price ($)
          <input type="number" min={0} max={1000000} step="0.01" value={amount}
            required onChange={e=>setAmount(e.target.value)}/></label>}
        <label><input type="checkbox" checked={draft.usedInShop}
          onChange={e=>change({usedInShop:e.target.checked})}/>Used in Shop</label>
        <label><input type="checkbox" checked={draft.trackInventory}
          onChange={e=>change({trackInventory:e.target.checked})}/>Track Inventory Level</label>
        <div className="ecfs-products-editor-actions">
          <button type="submit" disabled={saving}>{saving?'Saving…':'Save Product'}</button>
          <button type="button" disabled={saving} onClick={()=>setDraft(null)}>cancel</button>
        </div>
      </form>}
      <div className="ecfs-products-filters" aria-label="Product filters">
        {['Enabled','Disabled','All'].map(v=><button key={v} type="button"
          className={state===v?'selected':''} onClick={()=>setState(v)}>{v}</button>)}
        {['Retail','Supplies'].map(v=><button key={v} type="button"
          className={kind===v?'selected':''}
          onClick={()=>setKind(cur=>cur===v?'All':v)}>{v}</button>)}
        <select value={category} aria-label="Product Category"
          onChange={e=>setCategory(e.target.value)}>
          <option value="">Product Category</option>
          {categories.map(c=><option key={c}>{c}</option>)}
        </select>
        <input type="search" aria-label="Search Products" value={query}
          placeholder="Search" onChange={e=>setQuery(e.target.value)}/>
        <button type="button" onClick={exportCsv}>CSV ↓</button>
        <select className="ecfs-products-order" aria-label="Sort products"
          value={order} onChange={e=>setOrder(e.target.value)}>
          {['Name A-Z','Name Z-A','Category'].map(v=><option key={v}>{v}</option>)}
        </select>
      </div>
      <div className="ecfs-products-table-scroll"><table className="ecfs-products-table">
        <thead><tr><th></th><th>Category</th><th>SKU</th><th>Product</th>
          <th>Count Every</th><th>Warn At</th><th>Ordered</th><th>Sold</th></tr></thead>
        <tbody>{filtered.map(p=><tr key={p.id}>
          <td><CatalogStatusSwitch label={p.title+' enabled'}
            enabled={p.enabled} busy={statusBusy===p.id}
            disabled={!canEdit||saving} onToggle={()=>void setProductEnabled(p)}/></td>
          <td>{p.category}</td><td>{p.sku}</td>
          <td><button type="button" disabled={!canEdit}
            className="ecfs-products-header-link" onClick={()=>start(p)}>
            {p.title}</button> <small>{price(p.priceCents)}</small></td>
          <td>{p.daysBetweenCounts??''}</td>
          <td>{p.warnAtQuantity==null?'':p.warnAtQuantity+' units'}</td>
          <td></td><td></td>
        </tr>)}</tbody>
      </table>{!filtered.length&&<p>No products match these filters.</p>}</div>
    </>}
  </section>;
}
